import { AIAgentSidebarDefinition } from 'app/modules/sidebar/models';
import { U21SideMenu } from 'app/shared/u21-ui/components/dashboard';
import { useGetAIResults } from 'app/modules/aiAgentResults/queries/useGetAIResults';
import { GenAiGroundingFromAgentOutputs } from 'app/modules/aiAgentTasks/components/GenAiGroundingFromAgentOutputs';
import { selectGroundingOnlineResearchEnabled } from 'app/shared/featureFlags/selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  U21Markdown,
  U21NoData,
  U21Section,
  U21Skeleton,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import styled from 'styled-components';
import { TaskCategory } from 'app/modules/aiAgents/aiAgentTaskCategories';
import { AiAgentCustomTaskPromptName } from 'app/modules/aiAgentTasks/responses';

interface Props {
  data: AIAgentSidebarDefinition['data'];
}

export const SidebarAIAgent = (props: Props) => {
  const {
    data: { id, type, agentID },
  } = props;

  const genAIAgentOutputsEnabled = useSelector(
    selectGroundingOnlineResearchEnabled,
  );

  const { data, isLoading } = useGetAIResults({
    relation_id: id,
    relation_type: type,
    include_raw_content: type === 'ALERT' ? true : undefined,
  });

  const signal = useMemo(
    () => data?.signals.find((i) => i.agent_id === agentID),
    [agentID, data],
  );

  const shouldShowGenAIOutputs = useMemo(
    () =>
      genAIAgentOutputsEnabled &&
      agentID === TaskCategory.ONLINE_RESEARCH_SIGNAL_AGENT &&
      Boolean(
        signal?.tasks.some(
          (t) =>
            t.prompt_id === AiAgentCustomTaskPromptName.CUSTOM_ONLINE_SEARCH,
        ),
      ),
    [agentID, genAIAgentOutputsEnabled, signal],
  );

  return (
    <U21SideMenu
      loading={isLoading}
      title={signal ? signal?.title : <U21Skeleton width={100} />}
    >
      {(() => {
        if (!signal || !signal.tasks.length) {
          return <U21NoData />;
        }
        return (
          <U21Spacer>
            {signal.tasks.map((i, idx) => (
              <StyledU21Section
                key={i.id}
                collapsible
                collapsed={idx > 0}
                disableTitleCase
                title={
                  <U21Typography variant="subtitle2">
                    {i.question}
                  </U21Typography>
                }
              >
                <U21Markdown>{i.answer}</U21Markdown>
                {shouldShowGenAIOutputs && i.raw_answer ? (
                  <GenAiGroundingFromAgentOutputs rawContent={i.raw_answer} />
                ) : null}
              </StyledU21Section>
            ))}
          </U21Spacer>
        );
      })()}
    </U21SideMenu>
  );
};

// quick and dirty solution to get the styling
// ok since design will change
const StyledU21Section = styled(U21Section)`
  background-color: ${(props) => props.theme.palette.background.neutral};
  // keep border but same color as background so content positioning is the same
  border-color: ${(props) => props.theme.palette.background.neutral};

  .MuiCardHeader-root {
    padding: 16px;
  }

  .MuiCardContent-root {
    padding-left: 16px;
    padding-right: 16px;
  }
  // give it higher specificity to override
  .MuiCardContent-root.MuiCardContent-root.MuiCardContent-root {
    padding-bottom: 16px;
  }
`;
